/* eslint-disable react/prop-types */
import axios from 'axios';
import Cookies from 'js-cookie';
import { FieldArray } from 'formik';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useState } from 'react';
// api
import ApiKey from '../../../api/ApiKey';
import BaseApi from '../../../api/BaseApi';
// components
import TextEditor from '../../components/TextEditor';
import SelectReact from '../../components/SelectReact';
import InputCheckbox from '../../components/InputCheckbox';
import TextEditorDraft from '../../components/TextEditorDraft';
import AsyncSelectReact from '../../components/AsyncSelectReact';
import SelectWithIdInOptions from '../../components/SelectWithIdInOptions';
// helpers
import { debounceAsync } from '../../../helpers';
// hooks
import { useWindowSize } from '../../../../Hooks/use-window-size';
import { debounce } from 'lodash';

const AdditionalInfoBlock = ({ errors, values, touched, setErrors, directories, handleChange, setFieldValue, setFieldTouched }) => {
  const { width } = useWindowSize();
  const { t } = useTranslation('global');
  const tokenKey = Cookies.get('tokenClient');

  const {
    langs = [],
    skill_list = [],
    lang_skills = [],
    visibilities = [],
    driver_categories = [],
  } = directories;

  const {
    skills = [],
    languages = [],
    visibility = '',
    driver_license = 0,
    about_yourself = '',
    driver_category = [],
    company_whitelist = [],
    company_blacklist = [],
  } = values;

  const [editorValues, setEditorValues] = useState(about_yourself || '');

  const getSelectOptions = async (endpoint, inputValue) => {
    if (inputValue.length < 2) {
      return [];
    }

    try {
      const response = await axios.post(
        BaseApi + endpoint,
        { query: inputValue },
        {
          headers: {
            key: ApiKey,
            token: tokenKey,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data.response?.length === 0) {
        throw new Error('No options found');
      }

      const options = response.data.response.map(position => ({
        value: position.id,
        label: position.name,
      }));

      return options;
    } catch (error) {
      if (endpoint === '/candidates/getCompanies') {
        return [{ value: inputValue, label: inputValue }];
      }

      console.error('Error fetching cities:', error);

      return [];
    }
  };

  const handleFieldChange = (field, selectedOptions, limit = null) => {
    if (limit && selectedOptions.length > limit) {

      return;
    }

    if (field === 'visibility') {
      setFieldValue('company_blacklist', []);
      setFieldValue('company_whitelist', []);
    }

    setFieldValue(field, selectedOptions);
  };

  const handleEditorChange = useCallback(value => {
    setEditorValues(value);

    debouncedHandleEditorChange(value);
  }, []); // eslint-disable-line

  const debouncedGetSelectOptions = debounceAsync(getSelectOptions, 500);

  const debouncedHandleEditorChange = debounce(value => {
    setFieldValue('about_yourself', value);
    setFieldTouched('about_yourself', true);
  }, 1000);

  useEffect(() => {
    if (width >= 768) {
      window.scrollTo({ top: 350, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 650, behavior: 'smooth' });
    }
  }, [width]);

  return (
    <>
      {
        width >= 768
          ? (
            <TextEditor
              // required
              value={editorValues}
              name='about_yourself'
              error={errors.about_yourself}
              touched={touched.about_yourself}
              minHeight={width >= 768 ? '240px' : '260px'}
              label={t('jobseekerEditProfile.aboutYourself')}
              handleBlur={achievements => handleEditorChange(achievements)}
              placeholder={t('jobseekerEditProfile.aboutYourselfPlaceholder')}
            />
          ) : (
            <TextEditorDraft
              // required
              value={editorValues}
              name='about_yourself'
              error={errors.about_yourself}
              touched={touched.about_yourself}
              minHeight={width >= 768 ? '240px' : '260px'}
              label={t('jobseekerEditProfile.aboutYourself')}
              handleChange={achievements => handleEditorChange(achievements)}
              placeholder={t('jobseekerEditProfile.aboutYourselfPlaceholder')}
            />
          )
      }
      <SelectReact
        isMulti
        isSearchable
        name='skills'
        value={skills}
        error={errors.skills}
        touched={touched.skills}
        label={t('jobseekerEditProfile.skills')}
        handleBlur={() => setFieldTouched('skills', true)}
        placeholder={t('jobseekerEditProfile.selectSkills')}
        options={skill_list.map(i => ({ value: i.id, label: i.name }))}
        noOptionsMessage={() => t('jobseekerEditProfile.noOptionsFound')}
        handleChange={selectedOptions => handleFieldChange('skills', selectedOptions, 12)}
      />
      <div className='mt-4'>
        <FieldArray
          name='languages'
          render={({ push, remove }) => (
            languages.map((i, index) => (
              <div key={index}>
                <div className='row'>
                  <SelectWithIdInOptions
                    value={i.lng}
                    options={langs}
                    className='col-lg-4'
                    required={i.lng || i.lng_skill}
                    name={`languages[${index}].lng`}
                    error={errors?.languages?.[index]?.lng}
                    handleChange={e => handleChange(e, index)}
                    label={t('jobseekerEditProfile.language')}
                    touched={touched?.languages?.[index]?.lng}
                    errorMessage={errors?.languages?.[index]?.lng}
                    defaultOption={t('jobseekerEditProfile.selectLanguage')}
                    handleBlur={() => setFieldTouched(`languages[${index}].lng`, true)}
                  />
                  <SelectWithIdInOptions
                    value={i.lng_skill}
                    className='col-lg-4'
                    options={lang_skills}
                    required={i.lng || i.lng_skill}
                    name={`languages[${index}].lng_skill`}
                    handleChange={e => handleChange(e, index)}
                    error={errors?.languages?.[index]?.lng_skill}
                    label={t('jobseekerEditProfile.proficiency')}
                    touched={touched?.languages?.[index]?.lng_skill}
                    errorMessage={errors?.languages?.[index]?.lng_skill}
                    defaultOption={t('jobseekerEditProfile.selectProficiency')}
                    handleBlur={() => setFieldTouched(`languages[${index}].lng_skill`, true)}
                  />
                  <div className='mb-4 col-lg-4 text-center'>
                    {
                      index === languages.length - 1 && languages.length < 5
                        ? (
                          <button
                            type='button'
                            className='btn btn-primary button1 mt-lg-2'
                            onClick={() => push({ lng: '', lng_skill: '' })}
                          >
                            {t('jobseekerEditProfile.addMoreButton')}
                          </button>
                        ) : (
                          <button
                            type='button'
                            onClick={() => remove(index)}
                            className='btn btn-danger button2 mt-lg-2'
                          >
                            {t('jobseekerEditProfile.removeButton')}
                          </button>
                        )
                    }
                  </div>
                </div>
              </div>
            ))
          )}
        />
      </div>
      <div className='mb-4 row align-items-center'>
        <div className='col-lg-4 form-outline mb-4 DashBoardInputBx DashBoardCreatBx'>
          <InputCheckbox
            name='driver_license'
            checked={driver_license}
            style={{ height: '68px' }}
            label={t('jobseekerEditProfile.driverLicense')}
            handleChange={async e => {
              const isChecked = e.target.checked;

              await setFieldValue('driver_license', isChecked);

              if (!isChecked) {
                setFieldValue('driver_category', []);
                const currentErrors = { ...errors };

                delete currentErrors.driver_category;

                await setErrors(currentErrors);
              }
            }}
          />
        </div>
        {
          driver_license != 0 && (
            <SelectReact
              isMulti
              required
              isSearchable
              className='col-lg-8'
              name='driver_category'
              value={driver_category}
              error={errors.driver_category}
              touched={touched.driver_category}
              label={t('jobseekerEditProfile.driverCategory')}
              placeholder={t('jobseekerEditProfile.selectDriverCategory')}
              noOptionsMessage={() => t('jobseekerEditProfile.noOptionsFound')}
              options={driver_categories.map(i => ({ value: i.id, label: i.name }))}
              handleChange={selectedOptions => handleFieldChange('driver_category', selectedOptions)}
              handleBlur={() => {
                setTimeout(() => {
                  setFieldTouched('driver_category', true);
                }, 200);
              }}
            />
          )
        }
        <SelectReact
          required
          isSearchable
          name='visibility'
          value={visibility}
          error={errors.visibility}
          touched={touched.visibility}
          label={t('jobseekerEditProfile.visibility')}
          handleBlur={() => setFieldTouched('visibility', true)}
          placeholder={t('jobseekerEditProfile.selectVisibility')}
          noOptionsMessage={() => t('jobseekerEditProfile.noOptionsFound')}
          options={visibilities.map(i => ({ value: i.id, label: i.name }))}
          handleChange={selectedOptions => handleFieldChange('visibility', selectedOptions)}
        />
        {
          visibility.value === '1' && (
            <AsyncSelectReact
              isMulti
              required
              name='company_whitelist'
              value={company_whitelist}
              error={errors.company_whitelist}
              touched={touched.company_whitelist}
              label={t('jobseekerEditProfile.organization')}
              placeholder={t('jobseekerEditProfile.enterOrganization')}
              loadingMessage={() => t('jobseekerEditProfile.pleaseWait')}
              handleBlur={() => setFieldTouched('company_whitelist', true)}
              handleChange={selectedOption => handleFieldChange('company_whitelist', selectedOption)}
              loadOptions={inputValue => debouncedGetSelectOptions('/candidates/getCompanies', inputValue)}
              noOptionsMessage={
                ({ inputValue }) => inputValue.length >= 2
                  ? t('jobseekerEditProfile.noOptionsFound')
                  : t('jobseekerEditProfile.enterOrganization')
              }
            />
          )
        }
        {
          visibility.value === '2' && (
            <AsyncSelectReact
              isMulti
              required
              name='company_blacklist'
              value={company_blacklist}
              error={errors.company_blacklist}
              touched={touched.company_blacklist}
              label={t('jobseekerEditProfile.organization')}
              placeholder={t('jobseekerEditProfile.enterOrganization')}
              loadingMessage={() => t('jobseekerEditProfile.pleaseWait')}
              handleBlur={() => setFieldTouched('company_blacklist', true)}
              handleChange={selectedOption => handleFieldChange('company_blacklist', selectedOption)}
              loadOptions={inputValue => debouncedGetSelectOptions('/candidates/getCompanies', inputValue)}
              noOptionsMessage={
                ({ inputValue }) => inputValue.length >= 2
                  ? t('jobseekerEditProfile.noOptionsFound')
                  : t('jobseekerEditProfile.enterOrganization')
              }
            />
          )
        }
      </div>
    </>
  );
};

export default AdditionalInfoBlock;
